// api.js - Axios instance with JWT authentication

import axios from 'axios';
import {
  getAccessToken,
  setAccessToken,
  isTokenExpired,
  getRefreshToken,
  setRefreshToken,
  clearSession,
} from './services/authService';

const api = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' },
});

// Track if we're currently refreshing to prevent multiple simultaneous refresh calls
let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

const onTokenRefreshed = (newToken) => {
  refreshSubscribers.forEach(callback => callback(newToken));
  refreshSubscribers = [];
};

const onRefreshFailed = () => {
  refreshSubscribers.forEach(callback => callback(null));
  refreshSubscribers = [];
};

// Request interceptor - attach access token
api.interceptors.request.use(
  (config) => {
    // Skip auth for certain endpoints
    if (config.skipAuthInterceptor) {
      delete config.skipAuthInterceptor;
      return config;
    }

    const token = getAccessToken();

    if (token && !isTokenExpired()) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Helper to dispatch API error events
const dispatchApiError = (type, message) => {
  window.dispatchEvent(new CustomEvent('api:error', {
    detail: { type, message, timestamp: Date.now() }
  }));
};

// Response interceptor - handle 401, network errors, and server errors
api.interceptors.response.use(
  (response) => {
    // Clear any previous error state on successful response
    window.dispatchEvent(new CustomEvent('api:recovered'));
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle network errors (no response from server)
    if (!error.response) {
      const message = error.message?.includes('Network Error')
        ? 'Unable to reach the server. Please check your internet connection.'
        : 'Connection to server failed. The server may be down.';
      dispatchApiError('network', message);
      return Promise.reject(error);
    }

    // Handle 502 Bad Gateway (backend not reachable by nginx)
    if (error.response?.status === 502) {
      dispatchApiError('502', 'The server is temporarily unavailable. Please try again in a moment.');
      return Promise.reject(error);
    }

    // Handle 503 Service Unavailable
    if (error.response?.status === 503) {
      dispatchApiError('503', 'The service is temporarily unavailable due to maintenance or overload.');
      return Promise.reject(error);
    }

    // Handle 504 Gateway Timeout
    if (error.response?.status === 504) {
      dispatchApiError('504', 'The server took too long to respond. Please try again.');
      return Promise.reject(error);
    }

    // Handle other 5xx errors
    if (error.response?.status >= 500) {
      dispatchApiError('server', 'An unexpected server error occurred. Please try again later.');
      return Promise.reject(error);
    }

    // Don't retry if:
    // - Not a 401 error
    // - Already retried this request
    // - This is a refresh or auth request
    if (
      error.response?.status !== 401 ||
      originalRequest._retry ||
      originalRequest.url?.includes('/auth/refresh') ||
      originalRequest.url?.includes('/auth/logout') ||
      originalRequest.url?.includes('/auth/login') ||
      originalRequest.url?.includes('/auth/register') ||
      originalRequest.url?.includes('/auth/password-login')
    ) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    // If already refreshing, queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        subscribeTokenRefresh((token) => {
          if (token) {
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
            resolve(api(originalRequest));
          } else {
            reject(error);
          }
        });
      });
    }

    isRefreshing = true;

    try {
      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        throw new Error('No refresh token');
      }

      // Attempt to refresh
      const response = await axios.post('/api/auth/refresh', { refreshToken });

      const { accessToken, refreshToken: newRefreshToken } = response.data;

      setAccessToken(accessToken);
      if (newRefreshToken) {
        setRefreshToken(newRefreshToken);
      }

      isRefreshing = false;
      onTokenRefreshed(accessToken);

      // Retry original request with new token
      originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;
      return api(originalRequest);
    } catch (refreshError) {
      isRefreshing = false;
      onRefreshFailed();

      // Refresh failed - clear session and redirect to login
      clearSession();

      // Dispatch custom event for App.js to handle redirect
      window.dispatchEvent(new CustomEvent('auth:sessionExpired'));

      return Promise.reject(refreshError);
    }
  }
);

export default api;
